﻿import {isNullOrEmpty, nanoid, translation} from "@2d-front-monorepo/core";
import {
	IMvvmContextState,
	MvvmContextValidationResult,
	MvvmDataProvider,
	MvvmDataProviderRef,
	MvvmOptionList,
	MvvmOptionListEntry,
	MvvmOptionListRenderProps,
	MvvmTextInput,
	useArrayOnChange,
	useInitializeMvvmContext,
	useSelected
} from "@2d-front-monorepo/design-library";
import {
	AvailablePaymentMethods,
	CreditCardProvider,
	PaymentMethod,
	paymentMethodLabel,
	paymentMethodName,
	PermitsMvvmErrorCollector, UndefinedPaymentMethod
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import React, {JSX, ReactElement, ReactNode, RefObject, SyntheticEvent, useCallback, useEffect, useMemo, useRef} from "react";
import {useCreditCardAssignment} from "../hooks/useCreditCardAssignment";
import {AddUpdateCreditCard} from "./AddUpdateCreditCard";
import MaterialIcon from "./MaterialIcon";

type RenderPaymentMethodProps = {
	entry: MvvmOptionListEntry<PaymentMethod>;
	name: string;
	arrayKey: string;
	checked: boolean;
	onChange?: (e: SyntheticEvent<HTMLInputElement>) => void;
	renderAdditionalInformation: (method: PaymentMethod) => ReactNode;
}

type RenderPaymentMethodsProps = MvvmOptionListRenderProps<PaymentMethod> & {
	renderAdditionalInformation: (method: PaymentMethod) => ReactNode;
}

function createPaymentMethod(selected: AvailablePaymentMethods, creditCardPan: string | undefined): AvailablePaymentMethods {
	switch (selected.method) {
		case PaymentMethod.DebitCreditCard:
			return {method: PaymentMethod.DebitCreditCard, creditCardPan: selected.creditCardPan ?? creditCardPan ?? ""} as AvailablePaymentMethods;

		case PaymentMethod.Check:
			return {method: PaymentMethod.Check, checkNumber: selected.checkNumber ?? ""} as AvailablePaymentMethods;

		default:
			// case PaymentMethod.BankTransfer:
			return {method: selected.method} as AvailablePaymentMethods;
	}

}

function ensureFullObject(currentlySelected: AvailablePaymentMethods, creditCardPan: string | undefined): void {
	switch (currentlySelected.method) {
		case PaymentMethod.Check:
			if (currentlySelected.checkNumber == null) currentlySelected.checkNumber = "";
			return;

		case PaymentMethod.DebitCreditCard:
			if (currentlySelected.creditCardPan == null) currentlySelected.creditCardPan = creditCardPan ?? "";
			return;
		default:
			return;
	}
}

function getPaymentMethodIcon(payWith: PaymentMethod): string {
	switch (payWith) {
		case PaymentMethod.DebitCreditCard:
		case PaymentMethod.BackofficeDebitCreditCard:
			return "credit_card";

		case PaymentMethod.Paypal:
		case PaymentMethod.Coupon:
			return "";

		case PaymentMethod.Cash:
			return "local_atm";

		case PaymentMethod.Oxxo:
			return "";

		case PaymentMethod.Check:
			return "checkbook";

		case PaymentMethod.MoneyOrder:
			return "request_quote";

		case PaymentMethod.ApplePay:
			return "";

		case PaymentMethod.BankTransfer:
			return "account_balance";

		case PaymentMethod.PayrollCharge:
		case PaymentMethod.FarmaciasAhorro:
		case PaymentMethod.FarmaciasBenavides:
		case PaymentMethod.GooglePay:
		case PaymentMethod.SevenEleven:
		case PaymentMethod.TransferAdjustment:

		default:
			return "";
	}
}

interface PaymentMethodProps {
	context: IMvvmContextState<PaymentMethodComponentState>;
}

function RenderBankTransfer(props: PaymentMethodProps): JSX.Element {
	// const {context} = props;
	//TODO: this needs to show an attachment control
	return (
		<></>
	);
}

function RenderCheck(props: PaymentMethodProps): JSX.Element {
	const {context} = props;
	return (
		<div className={"card card--padded"}>
			<MvvmTextInput context={context}
			               property={"selected.checkNumber"}
			               type={"text"}
			               label={translation(PermitTranslations.payment_page__payment_method__check_number)}
			               column={"tabular-nums"}
			               inlineError={false}/>
		</div>
	);
}

function RenderPaymentMethod(props: RenderPaymentMethodProps): JSX.Element {
	const {name, entry, arrayKey, onChange, checked, renderAdditionalInformation} = props;
	const {label, hint, description, value, disabled} = entry;
	const controlId = useMemo(() => "ctrl-" + nanoid(), []);

	return <div className={`card payment-methods-entry${disabled ? " payment-methods-entry--disabled" : ""}${checked ? " payment-methods-entry--selected" : ""}`}>
		<div className="payment-methods-entry--content">
			<div className="payment-methods-entry--text">
				{!disabled && <label className="payment-methods-entry--label" htmlFor={controlId} title={label}></label>}
				<MaterialIcon icon={hint!}/>
				<div>
					<span>{label}</span>
					{description && <span>{description}</span>}
				</div>
			</div>
			<div className="payment-methods-entry--additional-information">
				{renderAdditionalInformation(value)}
			</div>
		</div>
		<div className="payment-methods-entry--selector">
			<input type="radio"
			       id={controlId}
			       name={name}
			       onChange={onChange}
			       value={value}
			       data-array-key={arrayKey}
			       tabIndex={disabled ? -1 : undefined}
			       disabled={disabled}
			       checked={checked}/>
		</div>
	</div>;
}

function RenderPaymentMethods(props: RenderPaymentMethodsProps): JSX.Element {
	const {
		name,
		values,
		items,
		onChange,
		renderAdditionalInformation
	} = props;

	const innerRef = useRef<HTMLDivElement>(null);
	const selected = useSelected(values, false);
	const internalOnChange = useArrayOnChange(innerRef, onChange);

	return (<div className={"layout vertical gap payment-methods__list"} ref={innerRef}>
		{items.map((item, index) => <RenderPaymentMethod
			key={index}
			entry={item}
			name={name}
			arrayKey={index.toString()}
			checked={selected(index.toString())}
			onChange={internalOnChange}
			renderAdditionalInformation={renderAdditionalInformation}/>)}
	</div>);
}

export type PaymentMethodComponentProps = {
	available: PaymentMethod[];
	onSubmit: (state: PaymentMethodComponentState) => Promise<void>;
	total: number;
	plates?: string[];
	default?: PaymentMethod;
	contextRef?: RefObject<MvvmDataProviderRef<PaymentMethodComponentState>>;
	children?: JSX.Element | JSX.Element[];
	className?: string;

}

export type PaymentMethodHeaderProps = {
	children?: ReactNode;
}

export function PaymentMethodHeader(props: PaymentMethodHeaderProps): JSX.Element {
	return <>{props.children}</>;
}

export type PaymentMethodComponentState = {
	selected: AvailablePaymentMethods;
}

export function PaymentMethodComponent(props: PaymentMethodComponentProps): JSX.Element {
	const {available, onSubmit, total, default: defaultPayment, contextRef, className, children, plates} = props;
	const klassName = "payment-methods" + (className ? ` ${className}` : "");
	const formRef = useRef<HTMLFormElement>(null);
	const autoSubmitted = useRef(false);
	const creditCardAssignment = useCreditCardAssignment("payment", plates);

	const submitValidation = useCallback(async function (model: PaymentMethodComponentState): Promise<MvvmContextValidationResult<PaymentMethodComponentState>[]> {
		const errors: MvvmContextValidationResult<PaymentMethodComponentState>[] = [];
		if (total === 0) {
			return errors;
		}

		const selected = model.selected;
		const selectedMethod = selected.method;

		if (selectedMethod === PaymentMethod.Undefined) {
			errors.push({path: "selected.method", error: translation(PermitTranslations.payment_page__payment_method__required)});
		}

		if (selectedMethod === PaymentMethod.DebitCreditCard) {
			if (creditCardAssignment == null) {
				errors.push({path: "selected.creditCardPan", error: translation(PermitTranslations.payment_page__payment_method__credit_card__required)});
			} else if (creditCardAssignment.creditCard.provider === CreditCardProvider.Undefined) {
				await creditCardAssignment.registerCreditCard();
			}
		}

		if (selectedMethod === PaymentMethod.Check && isNullOrEmpty(selected.checkNumber)) {
			errors.push({path: "selected.checkNumber", error: translation(PermitTranslations.payment_page__payment_method__check_number__required)});
		}

		return errors;
	}, [total, creditCardAssignment]);

	const {context} = useInitializeMvvmContext<PaymentMethodComponentState>({
		initialState: () => {
			return {
				selected: {
					method: defaultPayment ?? PaymentMethod.Undefined
				} as UndefinedPaymentMethod
			};
		},
		contextRef: contextRef,
		submitValidation
	});

	const currentlySelected = context.state.selected;

	const paymentMethods = useMemo<MvvmOptionListEntry<PaymentMethod>[]>(() => {
		return available.map(function (x) {
			const dKey = `payment-method__${paymentMethodName(x)}--description`;
			const description = translation(dKey);
			return {
				label: translation(paymentMethodLabel(x)),
				hint: getPaymentMethodIcon(x),
				description: dKey === description ? undefined : description,
				value: x
			};
		})
	}, [...available]);

	ensureFullObject(currentlySelected, creditCardAssignment.creditCard.number);

	const onSubmitInternal = useCallback(function (state: PaymentMethodComponentState): Promise<void> {
		state.selected = createPaymentMethod(state.selected, creditCardAssignment.creditCard.number);
		return onSubmit(state);
	}, [onSubmit, creditCardAssignment]);

	useEffect(() => {
		// A free permit (total === 0) has nothing for the user to pick, so the payment step
		// auto-completes instead of making them press the button (client requirement). This is
		// a one-shot "skip this step" action, NOT a reactive effect: the ref guard makes it
		// fire at most once ever, so it can never race with or duplicate a real submit no
		// matter how `total` changes afterwards. requestSubmit() is intentional - it runs the
		// exact same validated submit pipeline a user click would.
		if (total === 0 && !autoSubmitted.current) {
			autoSubmitted.current = true;
			formRef.current?.requestSubmit();
		}
	}, [total]);

	const renderAdditionalInformation = useCallback(function (method: PaymentMethod): ReactNode {
		const ccProvider = creditCardAssignment.creditCard.provider;

		switch (method) {
			case PaymentMethod.DebitCreditCard:
				return ccProvider == null || ccProvider == CreditCardProvider.Undefined
					? undefined
					: <>
						<div>Saved cards</div>
						<AddUpdateCreditCard allowUpdate={true} creditCardAssignment={creditCardAssignment}/>
						{/*<div>Add new card</div>*/}
					</>;

			case PaymentMethod.Check:
				return <RenderCheck context={context}/>;

			case PaymentMethod.BankTransfer:
				return <RenderBankTransfer context={context}/>;

			default:
				return null;
		}
	}, [context, plates, creditCardAssignment]);

	const renderList = useCallback(function (renderProps: MvvmOptionListRenderProps<PaymentMethod>): JSX.Element {
		return <RenderPaymentMethods {...renderProps} renderAdditionalInformation={renderAdditionalInformation}/>;
	}, [renderAdditionalInformation]);

	const childArray = React.Children.toArray(children) as ReactElement[];
	const headerChild = childArray.find(c => c.type === PaymentMethodHeader);
	const otherChildren = childArray.filter(c => c.type !== PaymentMethodHeader);

	return (
		<MvvmDataProvider<PaymentMethodComponentState> onSubmit={onSubmitInternal} context={context} className={klassName} ref={formRef}>
			<PermitsMvvmErrorCollector captureErrors={true}/>
			{headerChild}

			{total <= 0
				? undefined
				: <div className={"payment-methods__body"}>
					<div className={"payment-methods__options"}>
						<p>{translation(PermitTranslations.payment_page__payment_method)}</p>
						<MvvmOptionList context={context} property="selected.method" items={paymentMethods} render={renderList} onChange={() => context.clearErrors()}/>
					</div>
					{/*<div className={"payment-methods__details"}>*/}
					{/*	<dl className={"card"}>*/}
					{/*		<dt>{translation(PermitTranslations.total)}</dt>*/}
					{/*		<dd className={"tabular-nums"}>{formatCurrency(total)}</dd>*/}
					{/*	</dl>*/}
					{/*</div>*/}
				</div>
			}
			{otherChildren}
		</MvvmDataProvider>
	);
}
